/**  @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import classNames from 'classnames';

import { duration as formatDuration } from 'js/utils/DateTimeUtils';

import type { LearningEffortFirstPlayContextType } from 'bundles/academic-integrity/components/learning-effort/LearningEffortFirstPlayContext';
import { useLearningEffortFirstPlayContext } from 'bundles/academic-integrity/components/learning-effort/LearningEffortFirstPlayContext';
import type { QuizQuestionPrompt } from 'bundles/compound-assessments/types/FormParts';
import IVQMarker from 'bundles/video-player/components/progressBar/IVQMarker';
import ProgressBarTimeBadge from 'bundles/video-player/components/progressBar/ProgressBarTimeBadge';
import { NOT_SET } from 'bundles/video-player/constants';
import type { VideoPlayerEventingV3UIFunction } from 'bundles/video-player/types/VideoPlayer';
import type { InVideoQuestion } from 'bundles/video-quiz/index';

import _t from 'i18n!nls/video-player';

const styles = {
  progressBar: css`
    height: 5px;
    width: calc(100% - 10px);
    position: absolute;
    top: 0;
    left: 5px;
    transform: translate(0, -100%);
    z-index: 999;

    @media (forced-colors: active) {
      outline: 2px solid WindowText;

      .base-track {
        background-color: Window;
      }

      .hitbox {
        background-color: transparent;
      }

      .progress-track {
        background-color: Highlight;
      }

      .learner-max-video-progress-overlay {
        background-color: WindowText;
      }

      .progress-handle {
        background-color: Highlight;
      }
    }

    &:hover {
      cursor: pointer;
    }

    &:focus,
    &:active {
      outline: 2px solid #82a8e1;
    }
  `,
  modernProgressBar: css`
    width: calc(100%);
    left: 0;
  `,
  baseTrack: css`
    position: absolute;
    top: 50%;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: #a9a9a9;
    transform: translate(0, -50%) scaleY(0.6);
    transition: transform 0.1s cubic-bezier(0.4, 0, 1, 1);
  `,
  modernBaseTrack: css`
    background-color: var(--cds-color-neutral-background-primary-weak);
  `,
  hovering: css`
    transform: scale(1) translate(0, -50%);
  `,
  hitbox: css`
    position: absolute;
    top: 50%;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgb(0 0 0 / 0%);
    transform: translate(0, -50%);
  `,
  hitboxHovering: css`
    height: 500%;
  `,
  progressTrack: (widthAsPercent: string) => css`
    position: absolute;
    height: 100%;
    background-color: #3cb371;
    z-index: 2;
    width: ${widthAsPercent};
  `,
  modernProgressTrack: css`
    background-color: var(--cds-color-blue-500);
  `,
  maxVideoProgressOverlay: (widthAsPercent: string) => css`
    background: var(--cds-color-neutral-primary-invert);
    height: 100%;
    position: relative;
    z-index: 1;
    width: ${widthAsPercent};
  `,
  progressHandle: (leftOffsetAsPercent: string) => css`
    position: absolute;
    width: 0;
    height: 0;
    border-radius: 50%;
    top: 50%;
    background-color: #3cb371;
    transform: translate(-50%, -50%);
    transition: height 0.1s ease, width 0.1s ease;
    left: ${leftOffsetAsPercent};
  `,
  modernProgressHandle: css`
    background-color: var(--cds-color-blue-500);
  `,
  progressHandleHovering: css`
    width: 13px;
    height: 13px;
  `,
  modernProgressHandleHovering: css`
    width: 16px;
    height: 16px;
  `,
};

type BaseProps = {
  player: any;
  videoQuizQuestions: InVideoQuestion<QuizQuestionPrompt>[] | null;
  trackVideoPlayerClickEvent: VideoPlayerEventingV3UIFunction;
  trimmedStartTimeSeconds: number;
};

type FirstPlayThroughProps = Pick<
  LearningEffortFirstPlayContextType,
  'shouldDisableSkippingForward' | 'learnerMaxVideoProgressInSeconds' | 'setShowVideoSkippingDisabledNotification'
>;

type Props = BaseProps & FirstPlayThroughProps;

type ActionStateName = 'none' | 'hovering' | 'scrubbing';

type ActionStateOptions = {
  hoverFractionOffsetFromStart?: number | null;
  scrubbingFractionOffsetFromStart?: number | null;
  wasVideoPausedWhenScrubbingStarted?: boolean | null;
};

type NoState = {
  actionStateType: 'none';
};

type HoveringState = {
  actionStateType: 'hovering';
  hoverFractionOffsetFromStart?: number;
};

type ScrubbingState = {
  actionStateType: 'scrubbing';
  scrubbingFractionOffsetFromStart?: number;
  wasVideoPausedWhenScrubbingStarted?: boolean;
};

type ActionState = NoState | HoveringState | ScrubbingState;

type State = {
  currentTime: number | null;
  duration: number | null;
  actionState: ActionState;
};

export class VideoProgressBarBaseComponent extends React.Component<Props, State> {
  declare baseElement: HTMLElement | null | undefined;

  state: State = {
    actionState: {
      actionStateType: 'none',
    },
    currentTime: null,
    duration: null,
  };

  componentDidMount() {
    const { player } = this.props;

    this.setState({
      currentTime: this.getCurrentTime(),
      duration: player.duration(),
    });

    player.on('timeupdate', this.onTimeChange);
    player.on('durationchange', this.onDurationChange);

    document.addEventListener('mouseup', this.onDocumentMouseUp);
  }

  componentWillUnmount() {
    const { player } = this.props;

    player.off('timeupdate', this.onTimeChange);
    player.off('durationchange', this.onDurationChange);

    document.removeEventListener('mouseup', this.onDocumentMouseUp);
  }

  // the videoJs player uses the currentTime, relative to the whole video.
  // this UI / controlbar (saved in this component's state) uses the currentTime relative to the trimmed range.
  getCurrentTime = () => {
    const { trimmedStartTimeSeconds, player } = this.props;
    if (!trimmedStartTimeSeconds) {
      return player.currentTime();
    }

    return player.currentTime() - trimmedStartTimeSeconds;
  };

  onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    // slightly annoying exception to map approach below
    // stops page scroll from happening by default on space
    if (event.key === ' ' || event.key === 'Spacebar') {
      event.preventDefault();
      return;
    }

    const { shouldDisableSkippingForward, learnerMaxVideoProgressInSeconds, setShowVideoSkippingDisabledNotification } =
      this.props;

    const skipForward = () => {
      // Don't allow skipping forward if the new time after skipping exceeds the learner's maximum watched progress
      // if shouldDisableSkippingForward is true
      if (!shouldDisableSkippingForward || this.getCurrentTime() + 5 < learnerMaxVideoProgressInSeconds) {
        this.skip(5);
      } else {
        setShowVideoSkippingDisabledNotification(true);
      }
    };

    const keyHandlerMap = {
      // skip forward a little
      Up: skipForward,
      ArrowUp: skipForward,
      Right: skipForward,
      ArrowRight: skipForward,
      // skip backward a little
      Down: () => {
        this.skip(-5);
      },
      ArrowDown: () => {
        this.skip(-5);
      },
      Left: () => {
        this.skip(-5);
      },
      ArrowLeft: () => {
        this.skip(-5);
      },
    };
    // @ts-expect-error ts-migrate(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
    if (keyHandlerMap[event.key]) {
      // @ts-expect-error ts-migrate(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
      keyHandlerMap[event.key]();
    }
  };

  onTimeChange = () => {
    this.setState({
      currentTime: this.getCurrentTime(),
    });
  };

  onDurationChange = () => {
    const { player } = this.props;
    this.setState({
      duration: player.duration(),
    });
  };

  // needed to prevent focus outline on click
  // NOTE: lint rule is trying to force the handler to be above the instance field "baseElement", not nice
  onMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    event.preventDefault();
    this.beginScrubbing(event);
  };

  onMouseEnter = (event: React.MouseEvent<HTMLDivElement>) => {
    const {
      actionState: { actionStateType, ...remainingData },
    }: any = this.state;

    let type = actionStateType;

    const offset = this.getProgressBarFractionOffsetFromEvent(event);
    const additionalData: ActionStateOptions = remainingData;

    if (type === 'scrubbing') {
      additionalData.scrubbingFractionOffsetFromStart = offset;
    } else {
      type = 'hovering';
      additionalData.hoverFractionOffsetFromStart = offset;
    }

    this.setActionState(type, additionalData);
  };

  onMouseLeave = () => {
    const { duration, actionState } = this.state;

    if (actionState.actionStateType === 'scrubbing') {
      const { player, trimmedStartTimeSeconds } = this.props;
      const { scrubbingFractionOffsetFromStart } = actionState;
      if (duration && scrubbingFractionOffsetFromStart) {
        const currentTimeForPlayer = duration * scrubbingFractionOffsetFromStart + (trimmedStartTimeSeconds ?? 0);
        player.currentTime(currentTimeForPlayer);

        const currentTimeForDisplay = duration * scrubbingFractionOffsetFromStart;

        this.setState({
          currentTime: currentTimeForDisplay,
        });
      }
    } else {
      this.setActionState('none');
    }
  };

  onMouseMove = (event: React.MouseEvent<HTMLDivElement>) => {
    const {
      actionState: { actionStateType, ...remainingData },
    } = this.state;

    const offset = this.getProgressBarFractionOffsetFromEvent(event);

    if (actionStateType === 'hovering') {
      this.setActionState(actionStateType, { ...remainingData, hoverFractionOffsetFromStart: offset });
    } else if (actionStateType === 'scrubbing') {
      this.setActionState(actionStateType, { ...remainingData, scrubbingFractionOffsetFromStart: offset });
    }
  };

  onDocumentMouseUp = () => {
    const {
      actionState: { actionStateType },
    } = this.state;
    if (actionStateType === 'scrubbing') {
      this.endScrubbing();
    }
  };

  // Checks to determine whether scrubbing forward is allowed based on the learner's progress
  getIsScrubbingAllowed = (
    barFractionOffset: number,
    duration: number | null | undefined,
    shouldDisableSkippingForward: boolean,
    learnerMaxVideoProgressInSeconds: number
  ): boolean => {
    return (
      // Allow learners (by returning true) to be able to skip forward if shouldDisableSkippingForward is false
      // or if they area of the video the learner is trying to skip to is within the time they've already watched
      !shouldDisableSkippingForward || barFractionOffset * (duration || 0) <= learnerMaxVideoProgressInSeconds
    );
  };

  // Checks to determine whether to show a notification that shows skipping forward is disabled
  getShouldShowVideoSkippingDisabledNotification = (
    barFractionOffset: number,
    duration: number | null | undefined,
    shouldDisableSkippingForward: boolean,
    learnerMaxVideoProgressInSeconds: number
  ): boolean => {
    return (
      // If a learner clicks on a video time that they haven't watched yet, a notification that shows skipping forward
      // is disabled will show up
      shouldDisableSkippingForward && barFractionOffset * (duration || 0) > learnerMaxVideoProgressInSeconds
    );
  };

  onMouseUp = (event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    event.preventDefault();

    const {
      player,
      shouldDisableSkippingForward,
      learnerMaxVideoProgressInSeconds,
      trackVideoPlayerClickEvent,
      setShowVideoSkippingDisabledNotification,
      trimmedStartTimeSeconds,
    } = this.props;
    const { duration } = this.state;

    const { currentTime } = this.state;
    let currentTimeForPlayer = currentTime;
    let currentTimeForDisplay = currentTime;
    const barFractionOffset = this.getProgressBarFractionOffsetFromEvent(event);

    // this operation will not be valid if player and duration are not initialized
    if (!!player && duration !== null && duration !== undefined) {
      if (barFractionOffset !== null && barFractionOffset !== undefined) {
        if (
          this.getIsScrubbingAllowed(
            barFractionOffset,
            duration,
            shouldDisableSkippingForward,
            learnerMaxVideoProgressInSeconds
          )
        ) {
          const defaultPreviousVideoPosition = trimmedStartTimeSeconds ? 0 : NOT_SET;
          const previousVideoPosition = currentTime ?? defaultPreviousVideoPosition;
          currentTimeForPlayer = barFractionOffset * duration + (trimmedStartTimeSeconds ?? 0);
          currentTimeForDisplay = barFractionOffset * duration;

          if (previousVideoPosition != null) trackVideoPlayerClickEvent('video_scrub_bar', { previousVideoPosition });
        }

        if (
          this.getShouldShowVideoSkippingDisabledNotification(
            barFractionOffset,
            duration,
            shouldDisableSkippingForward,
            learnerMaxVideoProgressInSeconds
          )
        ) {
          setShowVideoSkippingDisabledNotification(true);
        }
      }
    }
    player.currentTime(currentTimeForPlayer);
    this.endScrubbing();
    this.setState({
      currentTime: currentTimeForDisplay,
    });
  };

  getProgressBarFractionOffsetFromEvent = (event: React.MouseEvent<HTMLDivElement>): number | null => {
    // need base element to exist for this to be valid
    // should not be non-fatal even if null returned...
    if (this.baseElement) {
      const barCoordinates = this.baseElement.getBoundingClientRect();
      const barX = barCoordinates.left;
      const barWidth = barCoordinates.width;
      const eventX = event.clientX;
      const barFractionOffset = (eventX - barX) / barWidth;
      return barFractionOffset;
    }

    return null;
  };

  setActionState(name: ActionStateName, additionalData: ActionStateOptions = {}) {
    const { actionState: currentActionState } = this.state;

    const actionState: any = {
      ...currentActionState,
      ...additionalData,
      actionStateType: name,
    };

    this.setState({ actionState });
  }

  // TODO: should be a part of player state management
  skip = (timeToSkip: number) => {
    const { player, trimmedStartTimeSeconds } = this.props;
    const { currentTime, duration } = this.state;
    // make sure we have initialized state for this operation
    if (currentTime !== null && currentTime !== undefined && duration !== null && duration !== undefined) {
      const newTime = Math.max(0, Math.min(currentTime + timeToSkip + (trimmedStartTimeSeconds ?? 0), duration));
      player.currentTime(newTime);
    }
  };

  beginScrubbing = (event: React.MouseEvent<HTMLDivElement>) => {
    const { player, shouldDisableSkippingForward } = this.props;

    if (!shouldDisableSkippingForward) {
      this.setActionState('scrubbing', {
        scrubbingFractionOffsetFromStart: this.getProgressBarFractionOffsetFromEvent(event),
        wasVideoPausedWhenScrubbingStarted: player.paused(),
      });
    }
    player.pause();
  };

  endScrubbing = () => {
    const { player } = this.props;
    const { actionState }: any = this.state;
    const { wasVideoPausedWhenScrubbingStarted, scrubbingFractionOffsetFromStart }: ScrubbingState = actionState;

    player.trigger('scrubbed');

    if (!wasVideoPausedWhenScrubbingStarted && !player.ended()) {
      player.play();
    }

    this.setActionState('hovering', {
      hoverFractionOffsetFromStart: scrubbingFractionOffsetFromStart,
    });
  };

  fractionToWholeNumber(fraction: number) {
    return fraction * 100;
  }

  formatFraction(fraction: number) {
    return `${(Math.floor(fraction * 10000) / 100).toFixed(2)}%`;
  }

  assignRef = (el: HTMLDivElement | null) => {
    this.baseElement = el;
  };

  render() {
    const {
      videoQuizQuestions,
      learnerMaxVideoProgressInSeconds,
      shouldDisableSkippingForward,
      trimmedStartTimeSeconds,
    } = this.props;
    const {
      actionState: { actionStateType, ...actionStateOptions },
      duration,
      currentTime,
    } = this.state;

    // don't render if state hasn't initialized properly
    if (duration === null || duration === undefined || currentTime === null || currentTime === undefined) {
      return null;
    }

    const { hoverFractionOffsetFromStart, scrubbingFractionOffsetFromStart }: any = actionStateOptions;

    const progressFractionOffset = currentTime / duration;
    const progressPercentOffset = this.formatFraction(progressFractionOffset);
    const questions = videoQuizQuestions;

    let scrubbingOffset = progressPercentOffset;
    let ariaValueNow = this.fractionToWholeNumber(progressFractionOffset);

    const timeBadgeOffset =
      actionStateType === 'hovering' ? hoverFractionOffsetFromStart : scrubbingFractionOffsetFromStart;

    if (actionStateType === 'scrubbing') {
      if (scrubbingFractionOffsetFromStart !== null && scrubbingFractionOffsetFromStart !== undefined) {
        scrubbingOffset = this.formatFraction(scrubbingFractionOffsetFromStart);
        ariaValueNow = this.fractionToWholeNumber(scrubbingFractionOffsetFromStart);
      }
    }
    const hovering = actionStateType === 'hovering' || actionStateType === 'scrubbing';
    const formattedCurrentTime = formatDuration(progressFractionOffset * duration * 1000, 'mmm:sss');
    const formattedDuration = formatDuration(duration * 1000, 'mmm:sss');
    const ariaValueText = _t('Current time #{formattedCurrentTime}, Duration #{formattedDuration}', {
      formattedCurrentTime,
      formattedDuration,
    });
    const learnerMaxVideoProgressPercent = this.formatFraction(learnerMaxVideoProgressInSeconds / duration);

    return (
      <div
        tabIndex={0}
        role="slider"
        aria-label={_t('Video Progress')}
        aria-valuemin={0}
        aria-valuemax={100}
        dir="ltr" // the video progress slider should always be left to right, even in 'rtl' experience
        aria-valuenow={ariaValueNow}
        aria-valuetext={ariaValueText}
        className={classNames('rc-VideoProgressBar', 'horizontal-box', 'align-items-absolute-center')}
        ref={this.assignRef}
        onKeyDown={this.onKeyDown}
        onMouseDown={this.onMouseDown}
        onMouseEnter={this.onMouseEnter}
        onMouseLeave={this.onMouseLeave}
        onMouseMove={this.onMouseMove}
        onMouseUp={this.onMouseUp}
        data-testid="video-progress-bar"
        css={[styles.progressBar, styles.modernProgressBar]}
      >
        <div
          css={[styles.baseTrack, hovering && styles.hovering, styles.modernBaseTrack]}
          className={classNames('base-track', { hovering })}
        >
          {questions &&
            questions.map((question) => (
              <IVQMarker
                key={`ivq-${question.id}`}
                question={question}
                // this duration comes from videojs which is already updated with the trimmed duration
                videoDurationSeconds={duration}
                trimmedStartTimeSeconds={trimmedStartTimeSeconds}
              />
            ))}
          <div
            css={[styles.progressTrack(scrubbingOffset), styles.modernProgressTrack]}
            className={classNames('progress-track', { hovering })}
          />
          {shouldDisableSkippingForward && (
            <div
              css={styles.maxVideoProgressOverlay(learnerMaxVideoProgressPercent)}
              className={classNames('learner-max-video-progress-overlay')}
            />
          )}
        </div>
        <div className={classNames('hitbox', { hovering })} css={[styles.hitbox, hovering && styles.hitboxHovering]} />
        <div
          css={[
            styles.progressHandle(scrubbingOffset),
            styles.modernProgressHandle,
            hovering && styles.progressHandleHovering,
            hovering && styles.modernProgressHandleHovering,
          ]}
          className={classNames('progress-handle', { hovering })}
        />
        {hovering && timeBadgeOffset && (
          <ProgressBarTimeBadge time={timeBadgeOffset * duration} videoDuration={duration} />
        )}
      </div>
    );
  }
}

const VideoProgressBar = (props: BaseProps) => {
  const { shouldDisableSkippingForward, learnerMaxVideoProgressInSeconds, setShowVideoSkippingDisabledNotification } =
    useLearningEffortFirstPlayContext();
  return (
    <VideoProgressBarBaseComponent
      {...props}
      shouldDisableSkippingForward={shouldDisableSkippingForward}
      learnerMaxVideoProgressInSeconds={learnerMaxVideoProgressInSeconds}
      setShowVideoSkippingDisabledNotification={setShowVideoSkippingDisabledNotification}
    />
  );
};

export default VideoProgressBar;
